import { apiRequest } from './core/client';

// ============================================================================
// Vendor orders — four documents, four sets of numbers:
//   ANNOUNCED announcements what the vendor OFFERED (release blast / rep sheet)
//   ORDERED   orderLines  what we asked for        (confirmation / PO)
//   INVOICED  invoices    what they said they sent (invoice, priced)
//   RECEIVED  receipts    what we actually got     (acot-db mirror)
// Nothing derived is stored; the balance endpoint computes it.
// ============================================================================

export interface OrderRef {
  id: string;
  refType: string;
  value: string;
}

export type Resolution = 'exact' | 'fuzzy' | 'new_product' | 'unresolved';

/** One announced product. No quantities — this document has none. */
export interface AnnouncementLine {
  id: string;
  rawDesc: string;
  vendorSku: string | null;
  upc: string | null;
  itemnumber: string | null;
  /** Resolves once line-setup creates the product; new_product = needs setup. */
  pid: number | null;
  resolution: Resolution;
  resolutionNote: string | null;
  msrp: number | null;
  unitPrice: number | null;
}

/** A brand/line name with the agent's latest catalog check stamped on it. */
export interface ReleaseName {
  name: string;
  /** acot-db product_categories.cat_id once the line exists. */
  cat_id?: number | null;
  /** When the agent last checked; checked with no cat_id = needs setup. */
  checked_at?: string | null;
}

/** WHAT THEY OFFERED — the release announcement that opened the journey. */
export interface Announcement {
  id: string;
  orderId: string;
  announcedDate: string | null;
  releaseNames: ReleaseName[];
  /** 'external' = the product list lives on a linked sheet (see product_list_url ref). */
  lineCapture: 'captured' | 'partial' | 'external' | null;
  sourceMessageId: string | null;
  sourceAttachmentId: string | null;
  sourceFilename: string | null;
  notes: string | null;
  lines: AnnouncementLine[];
}

/** WHAT WE ASKED FOR. */
export interface OrderLine {
  id: string;
  rawDesc: string;
  vendorSku: string | null;
  upc: string | null;
  itemnumber: string | null;
  pid: number | null;
  resolution: Resolution;
  resolutionNote: string | null;
  qtyOrdered: number | null;
  unitPrice: number | null;
  qtyRefunded: number | null;
  lineStatus: string | null;
  sourceMessageId: string | null;
}

/** WHAT THEY SAID THEY SENT — unit cost is AS PRINTED on the invoice. */
export interface InvoiceLine {
  id: string;
  rawDesc: string;
  vendorSku: string | null;
  upc: string | null;
  itemnumber: string | null;
  pid: number | null;
  resolution: Resolution;
  resolutionNote: string | null;
  qtyInvoiced: number | null;
  unitCost: number | null;
  lineTotal: number | null;
}

export interface Invoice {
  id: string;
  orderId: string;
  receiptId: string | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  currency: string;
  productSubtotal: number | null;
  freight: number | null;
  duties: number | null;
  otherFees: number | null;
  otherFeesLabel: string | null;
  discount: number | null;
  discountCode: string | null;
  /** What we actually paid — the balance target. */
  total: number | null;
  sourceMessageId: string | null;
  sourceAttachmentId: string | null;
  sourceFilename: string | null;
  /**
   * Every document that fed this invoice. A confirmation body, a priced PDF and a
   * shipment manifest are equal candidates for the same INVOICED document — which
   * one carries prices vs quantities varies by vendor and platform.
   */
  sources: InvoiceSource[];
  /** Two sources disagreed. Only one set of lines survives, so this can't be recomputed. */
  sourceConflict: boolean;
  sourceConflictNote: string | null;
  notes: string | null;
  lines: InvoiceLine[];
}

export interface InvoiceSource {
  id: string;
  messageId: string | null;
  attachmentId: string | null;
  filename: string | null;
  docKind:
    | 'order_confirmation'
    | 'invoice_document'
    | 'shipment_manifest'
    | 'refund_notice'
    | 'portal'
    | 'other';
  /** What this source actually supplied. */
  contributed: 'prices' | 'quantities' | 'both' | 'corroboration';
  note: string | null;
}

/** WHAT WE ACTUALLY GOT — mirrored from acot-db. */
export interface ReceiptLine {
  id: string;
  rawDesc: string;
  vendorSku: string | null;
  upc: string | null;
  itemnumber: string | null;
  pid: number | null;
  qtyReceived: number | null;
  bookedCostEach: number | null;
  /** acot-db products.price at snapshot — feeds the price check. */
  sellingPrice: number | null;
}

export interface Receipt {
  id: string;
  orderId: string;
  receivingId: number;
  supplierId: number | null;
  acotStatus: number | null;
  bookedTotal: number | null;
  dateCreated: string | null;
  snapshotAt: string | null;
  /** Set once the corrected costs were written back into acot-db. */
  balancedAt: string | null;
  balancedNote: string | null;
  lines: ReceiptLine[];
}

export interface OrderEvent {
  id: string;
  eventType: string;
  eventDate: string | null;
  messageRefs: Array<{ message_id: string }>;
  attachmentIds: string[];
  summary: string | null;
  data: Record<string, unknown>;
  attachmentFlag: string | null;
  createdAt: string;
}

export interface OrderCounts {
  announcements: number;
  announcedLines: number;
  orderedLines: number;
  invoices: number;
  invoicedLines: number;
  receipts: number;
  receivedLines: number;
  /** Lines on any document still marked new_product — the setup queue. */
  newProductLines: number;
  /** Lines still unresolved with no pid — never matched against the catalog,
   *  so setup work until proven otherwise (a journey can't hide by deferring
   *  resolution to receiving). */
  unresolvedLines: number;
  /** Release names checked against the catalog and not found. */
  releaseNamesMissing: number;
}

/** A linked inbound package, mirrored live from EasyPost via packages.vendor_order_id. */
export interface OrderPackage {
  id: string;
  trackingCode: string;
  carrier: string | null;
  status: string;
  statusDetail: string | null;
  estDeliveryDate: string | null;
  deliveredAt: string | null;
  publicUrl: string | null;
  /** How a delivery problem was settled; set = no longer a problem. */
  problemResolution: string | null;
  problemResolvedAt: string | null;
}

// ============================================================================
// Nudges + uploaded documents — the manual channel.
// A task records Matt's input for the next orders run; derived facts are never
// written directly. The run drains open tasks first and stamps an outcome.
// ============================================================================

export type AgentTaskKind =
  | 'verify_setup'      // "I set these products/releases up — re-check the catalog"
  | 'process_document'  // parse an uploaded file (payload.document_id/filename)
  | 'process_email'     // read a manually-linked email (payload.message_id)
  | 'find_order_doc'    // re-hunt a missing document (payload.target)
  | 'confirm_balanced'; // "cost edits applied — re-mirror and mark balanced"

export interface AgentTask {
  id: string;
  orderId: string;
  kind: AgentTaskKind;
  payload: Record<string, unknown>;
  note: string | null;
  createdAt: string;
  processedAt: string | null;
  outcome: string | null;
}

/** Overview shape: open task with enough journey identity to render standalone. */
export interface OpenAgentTask extends AgentTask {
  vendor: string;
  label: string | null;
}

export type OrderDocHint = 'invoice' | 'order_form' | 'product_list' | 'duty_invoice' | 'other';

export interface OrderDocument {
  id: string;
  orderId: string;
  filename: string;
  mime: string | null;
  sizeBytes: number | null;
  sha256: string | null;
  storagePath: string;
  docHint: OrderDocHint | null;
  note: string | null;
  /** Where a fetched document came from; null for manual uploads. */
  sourceUrl: string | null;
  /** 'upload' = Matt via the Files control; 'fetch' = the agent pulled an allowlisted vendor link. */
  origin: 'upload' | 'fetch';
  uploadedAt: string;
  processedAt: string | null;
  outcome: string | null;
}

/**
 * The sidebar's WORK counts — server-computed beside the rows each view
 * renders, so a count can never disagree with the list behind it. Every field
 * is required on purpose: the optional-with-fallback phase only existed to
 * bridge a deploy, and a `?? 0` after that bridge masks a server regression
 * as a plausible number.
 */
export interface WorkCounts {
  /** Open agent→Matt actions (status 'open' only — answered rows are the run's). */
  actions: number;
  toOrder: number;
  toOrderOverdue: number;
  newSupplier: number;
  setup: number;
  decide: number;
  /** Undecided review rows — received, nothing bills it, no ruling yet. */
  investigate: number;
  /** Rows out with the warehouse for a recount, on receivings still open. */
  recount: number;
  move: number;
  documents: number;
  balance: number;
  /** Every flagged row (suspects included) — the queue's full size. */
  prices: number;
  /** Flagged rows whose pid's landed cost is settled everywhere — safe to price now. */
  pricesActionable: number;
  followUps: number;
  /** Recounts, credits not yet requested, delivery problems, orphan parcels. */
  followUpsNeedsYou: number;
  /** Credits requested, recaptures with the run, queued nudges. */
  followUpsWatching: number;
  /** Parcels needing a human: a failure state, or no journey at all. */
  problems: number;
  packageProblems: number;
  /** Tracked parcels with no journey — counted at any delivery age, on purpose. */
  packagesUnlinked: number;
  /** What Matt can act on NOW: unattributed carrier bills + the balanced-
   *  without-duty watch list + unassessed verdicts. awaitingDuty rows are
   *  deliberately NOT counted (waiting, not work); the view still shows them. */
  duties: number;
  unattributedCarrierCharges: number;
  awaitingDuty: number;
  /** Balanced receivings with duty_expected and no attributed charge — waive or late bill. */
  dutyWatch: number;
  /** Receipts on duty-billing vendors with no verdict either way. */
  dutyUnassessed: number;
}

/**
 * A carrier duty/import bill — the fifth document. Billed by the SHIPPING
 * COMPANY (DHL) after arrival, never by the vendor; once attributed to a
 * receiving its amount joins that receiving's balance and distributes across
 * the arrived lines.
 */
export type CarrierChargeKind = 'duty' | 'freight' | 'bond' | 'other';

export interface CarrierCharge {
  id: string;
  carrier: 'dhl' | 'ups' | 'fedex' | 'wwex' | 'other';
  carrierInvoiceNo: string;
  carrierAccount: string | null;
  invoiceDate: string | null;
  amount: number | null;
  currency: string;
  kind: CarrierChargeKind;
  /** Air waybill = tracking number — the shipment join key, when known. */
  awb: string | null;
  orderId: string | null;
  receiptId: string | null;
  status: 'unattributed' | 'attributed' | 'dismissed';
  sourceMessageId: string | null;
  sourceAttachmentId: string | null;
  sourceDocumentId: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  /** Joined display context on list reads. */
  vendor: string | null;
  receivingId: number | null;
}

export async function getCarrierCharges(
  status?: 'unattributed' | 'attributed' | 'dismissed'
): Promise<CarrierCharge[]> {
  const qs = status ? `?status=${status}` : '';
  const result = await apiRequest<{ charges: CarrierCharge[] }>({
    path: `/orders/carrier-charges${qs}`,
    method: 'GET',
  });
  return result.charges;
}

/** Attribute to a receiving / detach / dismiss / edit fields. */
export async function updateCarrierCharge(
  id: string,
  body: {
    attach?: { receiving_id?: string | number; receipt_id?: string };
    detach?: boolean;
    dismiss?: boolean;
    kind?: CarrierChargeKind;
    awb?: string | null;
    amount?: number | null;
    invoice_date?: string | null;
    note?: string | null;
  }
): Promise<CarrierCharge> {
  const result = await apiRequest<{ charge: CarrierCharge }>({
    path: `/orders/carrier-charges/${id}`,
    method: 'PATCH',
    body,
  });
  return result.charge;
}

/** Set/waive the duty expectation on one receipt. false + note = the waive. */
export async function setReceiptDuty(
  receiptId: string,
  body: { expected: boolean | null; note?: string | null }
): Promise<void> {
  await apiRequest({ path: `/orders/receipts/${receiptId}/duty`, method: 'POST', body });
}

export interface OrderSummary {
  id: string;
  vendor: string;
  /** A few words naming what the order IS ("July release + restock") — the scan aid. */
  label: string | null;
  supplierId: number | null;
  /** Public path of the vendor's stored logo (see VendorMark); null shows the monogram. */
  logoUrl: string | null;
  poId: number | null;
  status: string;
  placedDate: string | null;
  /** Deadline to place the order (preorder window close) — the To-order urgency chip. */
  orderDueDate: string | null;
  /** When the vendor says it ships/releases — arrival pressure for setup. */
  expectedAvailability: string | null;
  /**
   * Sell embargo / street date: the first day this release's products may go
   * live on the shop. Distinct from expectedAvailability — vendors routinely
   * ship before the embargo lifts.
   */
  embargoDate: string | null;
  /** Derived: new_product lines anywhere, or a release name checked and not in the catalog. */
  needsSetup: boolean;
  currency: string;
  productionCompletionDate: string | null;
  newSupplierNeeded: boolean;
  /** How complete the ORDERED document is; 'unavailable' = the channel never itemises. */
  orderedCompleteness: 'complete' | 'partial' | 'unavailable' | null;
  orderedCompletenessNote: string | null;
  /**
   * The journey's money at a glance, in `currency`. Ordered value sums the
   * priced order lines net of refunds — null when no line carries a price, so
   * a channel that never itemises reads as unknown, not free. Invoiced value
   * sums the invoice totals. Both null for callers without orders:read:costs.
   */
  orderedTotal: number | null;
  invoicedTotal: number | null;
  notes: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  counts: OrderCounts;
  receivingIds: number[];
  /** null when the journey has no receipts yet. */
  allBalanced: boolean | null;
  /** Live (non-archived) linked packages — the summary's tracking chip. */
  packages: OrderPackage[];
  /** Nudges the next orders run has not consumed yet — the "pending" chip. */
  openTasks: number;
  /** Uploaded documents awaiting their process_document run. */
  pendingDocuments: number;
}

export interface OrderDetail extends OrderSummary {
  refs: OrderRef[];
  announcements: Announcement[];
  orderLines: OrderLine[];
  invoices: Invoice[];
  receipts: Receipt[];
  events: OrderEvent[];
  /** Unlike the summary, includes archived packages — the journey's full history. */
  packages: OrderPackage[];
  /** Every nudge ever recorded on this journey, processed ones included. */
  tasks: AgentTask[];
  /** Files uploaded straight onto the journey. */
  documents: OrderDocument[];
  /** Open and answered agent→Matt actions about this journey. */
  actions: OrderAction[];
}

/**
 * One product across all three documents — the shape the UI investigates with
 * ("we ordered 6, got 5: did they say they shipped 6, or was one refunded?").
 * Built client-side from a journey so the list and the detail agree.
 */
export interface ThreeWayLine {
  key: string;
  pid: number | null;
  itemnumber: string | null;
  vendorSku: string | null;
  desc: string;
  /** Present on the ANNOUNCED document (the vendor offered it). */
  announced: boolean;
  qtyOrdered: number | null;
  qtyRefunded: number | null;
  qtyInvoiced: number | null;
  unitCost: number | null;
  qtyReceived: number | null;
  bookedCostEach: number | null;
  resolution: Resolution | null;
  /** Set when two of the documents disagree; drives the UI highlight.
   *  'skipped' = announced but on none of the later documents — the coverage
   *  check ("you ordered 52 of the 70 announced SKUs"). */
  variance: 'short' | 'over' | 'not_received' | 'not_invoiced' | 'not_ordered' | 'skipped' | null;
}

function lineKey(l: { pid?: number | null; vendorSku?: string | null; upc?: string | null; itemnumber?: string | null; rawDesc?: string }): string {
  if (l.pid != null) return `pid:${l.pid}`;
  if (l.vendorSku) return `sku:${l.vendorSku.trim().toLowerCase()}`;
  if (l.upc) return `upc:${String(l.upc).replace(/^0+/, '')}`;
  if (l.itemnumber) return `itm:${l.itemnumber.trim().toLowerCase()}`;
  return `desc:${(l.rawDesc || '').trim().toLowerCase()}`;
}

/** Merge a journey's three documents into one row per product. */
export function buildThreeWay(order: OrderDetail): ThreeWayLine[] {
  const rows = new Map<string, ThreeWayLine>();
  const touch = (l: { pid?: number | null; vendorSku?: string | null; upc?: string | null; itemnumber?: string | null; rawDesc: string }): ThreeWayLine => {
    const key = lineKey(l);
    let row = rows.get(key);
    if (!row) {
      row = {
        key, pid: l.pid ?? null, itemnumber: l.itemnumber ?? null,
        vendorSku: l.vendorSku ?? null, desc: l.rawDesc, announced: false,
        qtyOrdered: null, qtyRefunded: null, qtyInvoiced: null, unitCost: null,
        qtyReceived: null, bookedCostEach: null, resolution: null, variance: null,
      };
      rows.set(key, row);
    }
    if (row.pid == null && l.pid != null) row.pid = l.pid;
    if (!row.itemnumber && l.itemnumber) row.itemnumber = l.itemnumber;
    if (!row.vendorSku && l.vendorSku) row.vendorSku = l.vendorSku;
    return row;
  };

  for (const ann of order.announcements ?? []) {
    for (const l of ann.lines) {
      const row = touch(l);
      row.announced = true;
      if (!row.resolution) row.resolution = l.resolution;
    }
  }
  for (const l of order.orderLines) {
    const row = touch(l);
    if (l.qtyOrdered != null) row.qtyOrdered = (row.qtyOrdered ?? 0) + l.qtyOrdered;
    if (l.qtyRefunded != null) row.qtyRefunded = (row.qtyRefunded ?? 0) + l.qtyRefunded;
    row.resolution = l.resolution;
  }
  for (const inv of order.invoices) {
    for (const l of inv.lines) {
      const row = touch(l);
      if (l.qtyInvoiced != null) row.qtyInvoiced = (row.qtyInvoiced ?? 0) + l.qtyInvoiced;
      if (l.unitCost != null) row.unitCost = l.unitCost;
      if (!row.resolution) row.resolution = l.resolution;
    }
  }
  for (const rc of order.receipts) {
    for (const l of rc.lines) {
      const row = touch(l);
      if (l.qtyReceived != null) row.qtyReceived = (row.qtyReceived ?? 0) + l.qtyReceived;
      if (l.bookedCostEach != null) row.bookedCostEach = l.bookedCostEach;
    }
  }

  const hasInvoice = order.invoices.length > 0;
  const hasReceipt = order.receipts.length > 0;
  for (const row of rows.values()) {
    const inv = row.qtyInvoiced;
    const rec = row.qtyReceived;
    const ord = row.qtyOrdered;
    if (inv != null && rec != null && inv !== rec) row.variance = rec < inv ? 'short' : 'over';
    else if (inv != null && rec == null && hasReceipt) row.variance = 'not_received';
    else if (rec != null && inv == null && hasInvoice) row.variance = 'not_invoiced';
    else if ((inv != null || rec != null) && ord == null && order.orderLines.length > 0) row.variance = 'not_ordered';
    // The announcement coverage check — only meaningful once an order exists;
    // before that every announced line is legitimately still "to be ordered".
    else if (row.announced && ord == null && inv == null && rec == null && order.orderLines.length > 0) {
      row.variance = 'skipped';
    }
  }
  return [...rows.values()];
}

// ============================================================================
// Balance — everything below is COMPUTED server-side from the three documents.
// ============================================================================

/** One receiving awaiting write-back, with what it still needs to be balanceable. */
export interface BalanceQueueRow {
  receiptId: string;
  orderId: string;
  receivingId: number;
  vendor: string;
  status: string;
  currency: string;
  bookedTotal: number | null;
  invoiceTotal: number | null;
  balancedAt: string | null;
  snapshotAt: string | null;
  counts: {
    receiptLines: number;
    invoices: number;
    invoiceLines: number;
    invoicesMissingTotal: number;
  };
  ready: boolean;
  /** Header-only-by-design vendor (Faire): not ready, but nothing outstanding. */
  accepted: boolean;
  /** What is missing when not ready. Route on `code`; `text` is display prose. */
  needs: ReceivingNeed[];
}

/** Balance-blocking gaps. The CODE is the contract — the UI routes and counts
 *  on it and sweeps filter on it; the text is prose the server may reword. */
export type ReceivingNeedCode =
  | 'invoice_missing'
  | 'invoice_no_lines'
  | 'invoice_total_missing'
  /** The run's own mirror work — never a Documents row. */
  | 'receipt_not_mirrored'
  /** The duty hold-open — the Duties view owns it, never Documents. */
  | 'duty_unattributed';

/** Real-but-not-blocking document gaps, same {code, text} shape. */
export type DocumentGapCode =
  | 'ordered_missing'
  | 'ordered_partial'
  | 'ordered_unverified'
  | 'invoice_source_conflict';

export interface ReceivingNeed {
  code: ReceivingNeedCode;
  text: string;
}

export interface DocumentGap {
  code: DocumentGapCode;
  text: string;
}

/**
 * A balance blocker: the same {code, text} shape as a need, plus the figures.
 * The UI keys its help text on `code` and prints `text` as the one-line fact.
 */
export interface Blocker {
  code: string;
  text: string;
  params?: Record<string, unknown>;
}

/** credited = supplier credits the missing units; absorbed = we ate it and its
 *  value spreads over what arrived; excluded = another order's line misfiled
 *  into this receiving (flagged until moved); recount = with the warehouse —
 *  our count may be wrong, so costing stays blocked until verified. */
/** `as_booked`: closed on a receiving balanced while it was open — the costs
 *  written stand, nothing re-spreads. Set only by a one-off migration. */
export type Disposition = 'credited' | 'absorbed' | 'excluded' | 'recount' | 'recapture' | 'as_booked';

/** The follow-up watch (credited/excluded). credited: pending = no ask seen
 *  yet, requested = claim went out, resolved = credit landed. excluded:
 *  pending = lines await the move to the right receiving, resolved = moved. */
export type ResolutionStatus = 'pending' | 'requested' | 'resolved';

/**
 * WHY the counts differ. `kind` says which way the numbers point; the cause
 * says what is actually behind it — and only two of these are count questions
 * at all. Assigned server-side per receiving, except `misfiled`, which only
 * exists once every receiving is in hand (the overview handler pairs them).
 */
export type DiscrepancyCause =
  /** Both sides booked units and disagree. The real count question. */
  | 'count'
  /** Ditto, but one side is an exact multiple of the other — invoiced 1 "(x10)"
   *  pack against 10 pieces booked. Routed to the orders run to recapture at
   *  the pid's grain; the run records `grain_checked` if the invoice is right. */
  | 'uom_suspect'
  /** Short here, over on another receiving: the units are next door. */
  | 'misfiled'
  /** Invoiced, nothing received, order still in transit. Not a discrepancy. */
  | 'awaiting_arrival'
  /** No invoice line to match, and nobody has read the invoice yet — agent work. */
  | 'unmatched_capture_pending'
  /** Searched; the mailbox has no itemised document. Only a human can get one. */
  | 'unmatched_capture_unavailable'
  /** This vendor bills header-only by design. Nothing to find. */
  | 'unmatched_never_itemised'
  /** Leftovers on both sides account for identical units — the invoice was
   *  captured coarser than the receiving books. Recapture fixes all at once. */
  | 'matching_failure'
  /** Received, the invoice has lines, none bills this. Rare; worth a look. */
  | 'unmatched_line'
  /** A received line whose BRAND is not this vendor's, with no offsetting short
   *  anywhere to pair it against — the ONE-SIDED misfile. `misfiled` needs both
   *  halves of a swap; this is a product that simply does not belong here and is
   *  missing from nowhere, so nothing pairs it, it auto-settles to
   *  excluded/inferred and the receiving reads balanced with a stranger inside
   *  it. Judged on the description's trailing brand segment (never the
   *  itemnumber prefix, which is a legacy catalog artefact), and only when the
   *  vendor carries that brand on no other receiving — a distributor that
   *  genuinely resells it repeats across receivings; a misfile happens once. */
  | 'brand_outlier';

/** What resolves the row — and therefore which list it belongs in. `settle` is
 *  the only one that is a decision, and the only one the discrepancies table
 *  asks about. */
export type DiscrepancyAction =
  | 'settle' | 'move' | 'review' | 'source_invoice' | 'capture_lines' | 'wait' | 'accept'
  /** Hand-offs — a recorded decision, not a cause. Both keep costing blocked and
   *  clear when the difference disappears rather than by a further decision. */
  | 'recount' | 'recapture';

export interface CountDiscrepancy {
  /** Merge key — what set_discrepancy_disposition takes as line_key. */
  key: string;
  pid: number | null;
  itemnumber: string | null;
  vendorSku: string | null;
  desc: string | null;
  /** Shelf location ("A-RRR-HHH") from the receipt mirror — a snapshot of
   *  where the product lived when the receiving was last mirrored. The count
   *  sheet does NOT print this: it fetches current locations live
   *  (getLiveProductLocations) at print time, since products get re-shelved. */
  location: string | null;
  qtyOrdered: number | null;
  qtyInvoiced: number;
  qtyReceived: number;
  difference: number;
  kind: 'short' | 'over' | 'not_received' | 'not_invoiced';
  cause: DiscrepancyCause;
  action: DiscrepancyAction;
  owner: 'matt' | 'agent' | 'none';
  /** cause === 'misfiled' only: the receiving holding the other half of the
   *  pair. 'from' = the units missing here are sitting there; 'to' = the
   *  surplus booked here belongs there. */
  misfiledCounterpart?: {
    receiptId: string;
    receivingId: number;
    vendor: string;
    direction: 'from' | 'to';
  } | null;
  valueAtTarget: number | null;
  /** What was decided. null means undecided — the receiving cannot be costed. */
  disposition: Disposition | null;
  /** 'recorded' = a real decision; 'inferred' = settled itself (zero-value, or never invoiced). */
  dispositionSource: 'recorded' | 'inferred' | 'undecided';
  creditAmount?: number | null;
  resolutionStatus?: ResolutionStatus | null;
  dispositionNote?: string | null;
  /** When the disposition (or the returned count) was recorded. */
  dispositionAt?: string | null;
  /** Undecided because a recount came back: `dispositionNote` holds what was counted. */
  counted?: boolean;
}

/** One row of the literal edit list for the receiving-edit grid. */
export interface CostEdit {
  pid: number | null;
  itemnumber: string | null;
  vendorSku: string | null;
  desc: string | null;
  qty: number;
  booked: number | null;
  target: number;
  deltaEach: number | null;
  deltaLine: number | null;
}

export interface ReceivingBalance {
  order: { id: string; vendor: string; supplierId: number | null; status: string; currency: string };
  receipt: {
    id: string;
    receivingId: number;
    bookedTotal: number | null;
    bookedTotalFromLines: number;
    snapshotAt: string | null;
    balancedAt: string | null;
    lineCount: number;
    /** Carrier duty hold-open: true blocks the balance until a charge attributes. */
    dutyExpected: boolean | null;
    dutyNote: string | null;
  } | null;
  /** Attributed carrier duty/import bills, folded into the distribution. */
  carrierCharges: Array<{
    id: string;
    carrier: string;
    carrierInvoiceNo: string;
    kind: CarrierChargeKind;
    amount: number | null;
    invoiceDate: string | null;
    awb: string | null;
    note: string | null;
  }>;
  invoices: Array<{
    id: string;
    invoiceNo: string | null;
    invoiceDate: string | null;
    currency: string;
    productSubtotal: number | null;
    freight: number | null;
    duties: number | null;
    otherFees: number | null;
    otherFeesLabel: string | null;
    discount: number | null;
    total: number | null;
    lineCount: number;
    source: { messageId: string | null; attachmentId: string | null; filename: string | null };
  }>;
  distribution: {
    productSubtotal: number | null;
    subtotalBasis: string;
    freight: number;
    duties: number;
    otherFees: number;
    discount: number;
    charges: number;
    /** Carrier duty bills attributed to this receiving — inside `charges`,
     *  broken out because the biller is the carrier, not the vendor. */
    carrierCharges?: number;
    invoiceTotal: number | null;
    factor: number | null;
    factorBasis: string | null;
    chargeCheck: { itemisedCharges: number; impliedCharges: number; gap: number; agrees: boolean } | null;
    /** Does each invoice's own line detail extend to its own stated subtotal? */
    lineCheck: {
      agrees: boolean;
      perInvoice: { invoiceNo: string | null; statedSubtotal: number; lineExtSum: number; gap: number; agrees: boolean }[];
    } | null;
    /** Penny-rounding cents settled onto specific lines so the receiving ties exactly. */
    residual?: {
      cents: number;
      lines: number;
      basis: string | null;
      appliedTo: { pid: number | null; itemnumber: string | null; desc: string | null; qty: number; adjustment: number }[];
      /** Sub-cent shifts made inside lines' existing cents so the stored header total ties too. */
      headerAdjustment: {
        cents: number;
        lines: number;
        settled: boolean;
        basis: string;
        appliedTo: { pid: number | null; itemnumber: string | null; desc: string | null; qty: number; costShift: number }[];
      } | null;
    };
    /** Each invoice's own factor — targets come from these, not the blend above. */
    perInvoice: Array<{
      invoiceNo: string | null;
      lineCount: number;
      productSubtotal: number | null;
      total: number | null;
      /** Freight billed on its own lineless invoice, spread over the goods invoices. */
      chargeOnly?: boolean;
      allocatedCharge?: number;
      factor: number | null;
    }>;
    /** True when the invoices on this receiving price at different structures. */
    mixedFactors: boolean;
  };
  totals: {
    lineCount: number;
    costEditLines: number;
    linesMissingUnitCost: number;
    countDiscrepancyLines: number;
    bookedTotal: number;
    /** Invoice total less supplier credits for short-shipped units — what we actually paid. */
    effectivePaid: number | null;
    /** Credits the supplier gave back for missing units. */
    supplierCredits: number;
    /** Attributed carrier duty/import money included in effectivePaid. */
    carrierCharges: number;
    /** Count discrepancies with no credited/absorbed decision yet; blocks costing. */
    undecidedDiscrepancies: number;
    /** ROUND(SUM(cost x qty),2) — what acot-db stores and the backend UI shows. */
    targetReceivingTotal: number;
    /** The same target under per-line rounding; differs only when 5dp costs pull them apart. */
    targetReceivingTotalLineSum: number;
    costDeltaTotal: number;
    invoicedAtTarget: number;
    distributionGap: number | null;
    countVariance: number;
    balanced: boolean;
  };
  countDiscrepancies: CountDiscrepancy[];
  costEdits: CostEdit[];
  linesMissingUnitCost: Array<{
    pid: number | null; itemnumber: string | null; desc: string | null;
    qty: number; booked: number; reason: string;
  }>;
  headerIds: { invoiceId: string | null; orderNos: string[] };
  blockers: Blocker[];
}

export async function getBalanceQueue(): Promise<{
  queue: BalanceQueueRow[];
  counts: { total: number; ready: number; blocked: number };
}> {
  return apiRequest({ path: '/orders/balance-queue', method: 'GET' });
}

export async function getReceivingBalance(
  params: { receivingId?: number; receiptId?: string }
): Promise<ReceivingBalance> {
  const query: Record<string, string> = {};
  if (params.receiptId) query.receipt_id = params.receiptId;
  else if (params.receivingId != null) query.receiving_id = String(params.receivingId);
  const response = await apiRequest<{ balance: ReceivingBalance }>({
    path: '/orders/balance',
    method: 'GET',
    query,
  });
  return response.balance;
}

export async function getOrders(
  options: { status?: string; vendor?: string; includeArchived?: boolean } = {}
): Promise<OrderSummary[]> {
  const query: Record<string, string> = {};
  if (options.status) query.status = options.status;
  if (options.vendor) query.vendor = options.vendor;
  if (options.includeArchived) query.include_archived = 'true';
  const response = await apiRequest<{ orders: OrderSummary[] }>({
    path: '/orders',
    method: 'GET',
    query: Object.keys(query).length ? query : undefined,
  });
  return Array.isArray(response.orders) ? response.orders : [];
}

export async function getOrder(id: string): Promise<OrderDetail | null> {
  const response = await apiRequest<{ order: OrderDetail }>({
    path: `/orders/${encodeURIComponent(id)}`,
    method: 'GET',
  });
  return response.order ?? null;
}

export async function archiveOrder(id: string): Promise<void> {
  await apiRequest({
    path: `/orders/${encodeURIComponent(id)}/archive`,
    method: 'POST',
    body: { archived: true },
  });
}

/**
 * Matt's "Won't order" button — dismiss a to-be-ordered journey (until he Reopens it).
 * Archives it with status wont_order; the journey's release fingerprint ref
 * keeps resolving, so the sweep skips the same announcement forever (a NEW
 * collection gets a new fingerprint and surfaces normally). UI-only: the
 * orders skill never sets this status.
 */
export async function dismissOrderWontOrder(id: string): Promise<void> {
  await apiRequest({
    path: `/orders/${encodeURIComponent(id)}/archive`,
    method: 'POST',
    body: { archived: true, status: 'wont_order' },
  });
}

/** Set the short scan label ("July release + restock") on an order. */
export async function setOrderLabel(id: string, label: string | null): Promise<void> {
  await apiRequest({
    path: `/orders/${encodeURIComponent(id)}`,
    method: 'PATCH',
    body: { label },
    query: { compact: 'true' },
  });
}

export interface DispositionEntry {
  line_key: string;
  disposition: Disposition | null;
  credit_amount?: number | null;
  resolution_status?: ResolutionStatus;
  pid?: number | null;
  note?: string | null;
  /** 'excluded' only: the receiving the misfiled units belong to, when known. */
  destination_receiving_id?: number | null;
}

/** Record what was decided about count discrepancies on one receiving. */
export async function setDiscrepancyDispositions(
  receiptId: string,
  dispositions: DispositionEntry[]
): Promise<void> {
  await apiRequest({
    path: `/orders/receipts/${encodeURIComponent(receiptId)}/discrepancies`,
    method: 'POST',
    body: { dispositions },
  });
}

// ============================================================================
// Overview — the whole reconciliation picture, computed server-side per call.
// ============================================================================

/** One mirrored receiving with its computed balance state. */
export interface OverviewReceiving {
  receiptId: string;
  orderId: string;
  receivingId: number;
  vendor: string;
  label: string | null;
  /** Best-known vendor contact — sender of the latest invoice (else
   *  announcement) source email. Prefills the Ask-vendor composes; editable. */
  vendorEmail: string | null;
  orderStatus: string;
  currency: string;
  dateCreated: string | null;
  snapshotAt: string | null;
  balancedAt: string | null;
  balancedNote: string | null;
  /** Does this shipment owe a carrier duty bill not on the vendor invoice?
   *  true holds the balance open until a charge is attributed; false = DDP/
   *  domestic or Matt's waive (dutyNote says which); null = unassessed. */
  dutyExpected: boolean | null;
  dutyNote: string | null;
  /** Attributed carrier charges, already folded into the distribution. */
  carrierCharges: Array<{
    id: string;
    carrier: string;
    carrierInvoiceNo: string;
    kind: CarrierChargeKind;
    amount: number | null;
    invoiceDate: string | null;
    awb: string | null;
    note: string | null;
  }>;
  bookedTotal: number | null;
  invoiceTotal: number | null;
  effectivePaid: number | null;
  targetTotal: number | null;
  costDeltaTotal: number | null;
  distributionGap: number | null;
  balanced: boolean;
  costEditLines: number;
  /** Count differences Matt must decide on. */
  undecidedDiscrepancies: number;
  /** Everything still holding costing up, whatever its cause — always >= the
   *  above. An unread invoice blocks the money without being Matt's to settle. */
  unsettledDifferences: number;
  blockers: Blocker[];
  discrepancies: CountDiscrepancy[];
  ready: boolean;
  /** Header-only-by-design vendor (Faire): not ready, but nothing outstanding. */
  accepted: boolean;
  needs: ReceivingNeed[];
  documentGaps: DocumentGap[];
}

/** Landed cost vs current selling price for one received product. */
export interface PriceCheckRow {
  receiptId: string;
  orderId: string;
  receivingId: number;
  vendor: string;
  pid: number | null;
  itemnumber: string | null;
  vendorSku: string | null;
  desc: string | null;
  qtyReceived: number;
  /** Landed cost per unit RECEIVED — the line's money spread over the units that
   *  actually landed, so a pack price billed against individual units costs each
   *  unit correctly. */
  cost: number;
  costBasis: 'target' | 'booked';
  /** Units the invoice billed, when known. Below qtyReceived means the invoice
   *  counted a coarser grain (packs) than the receiving did (eaches). */
  qtyInvoiced: number | null;
  /** qtyReceived / qtyInvoiced when that is a whole number — the pack size the
   *  vendor billed in. Null when the ratio is ragged (the cost is still spread
   *  correctly, there is just no clean pack to name). */
  packFactor: number | null;
  /** The uncorrected per-invoiced-unit cost, present only when it differs from
   *  `cost`. What the row would have shown before the spread. */
  invoicedCost: number | null;
  /** Current selling price (acot-db products.price at snapshot). */
  sellingPrice: number;
  /** sellingPrice / cost. Keystone is 2.0. */
  markup: number;
  /** Below x1 — we would sell every unit at a loss. Nearly always a cost
   *  captured at the wrong grain rather than a real pricing decision, so these
   *  are held out of the pricing list. */
  suspect: boolean;
  suspectReason: string | null;
  /**
   * Whether this landed cost is settled. False while the receiving still has
   * unsettled count differences, undistributed invoice value or open blockers —
   * any of which move the distribution factor and so re-price every line on it.
   * Pricing off a false one means pricing twice.
   */
  costFinal: boolean;
  costProvisionalReason: string | null;
  /**
   * "Keep as is" — Matt decided this markup stands (an MSRP that can't be
   * raised and stay competitive). Hidden from the pricing lists, but not
   * permanently: the dismissal is watermarked at the receivings that existed
   * when it was recorded, so receiving the product again puts it back.
   */
  dismissed: boolean;
}

/** An open follow-up on a decided discrepancy: a credit being watched, a
 *  misfiled line awaiting its move, or a recount with the warehouse. */
export interface PendingResolution {
  receiptId: string;
  orderId: string;
  receivingId: number;
  vendor: string;
  label: string | null;
  currency: string;
  lineKey: string;
  pid: number | null;
  disposition: Disposition;
  creditAmount: number | null;
  /** null for recount — the disposition itself is the open state. */
  resolutionStatus: ResolutionStatus | null;
  note: string | null;
  /** 'excluded' only: the receiving the misfiled units belong to, when known.
   *  The Move view renders from→to from THIS, never by parsing the note. */
  destinationReceivingId: number | null;
  updatedAt: string | null;
}

/**
 * A tracked parcel that resolved to no journey — physical goods no order
 * document explains. Handed over separately from `orders[].packages` because
 * having no journey is precisely the condition: it can never ride in on one.
 */
export interface UnlinkedPackage {
  id: string;
  trackingCode: string;
  carrier: string | null;
  vendor: string | null;
  orderRef: string | null;
  status: string;
  statusDetail: string | null;
  estDeliveryDate: string | null;
  deliveredAt: string | null;
  packageCount: number;
  itemsSummary: string | null;
  publicUrl: string | null;
  messageRefs: Array<{ message_id: string }>;
  createdAt: string;
}

/**
 * A receipt on a duty-billing vendor whose `duty_expected` was never set either
 * way. Only an explicit `true` holds a receiving open for the carrier's bill, so
 * null is not "no duty due" — it is nobody having decided. The vendor set is
 * derived server-side from real duty history, which keeps this to a handful.
 */
export interface DutyUnassessedReceipt {
  receiptId: string;
  orderId: string;
  receivingId: number;
  vendor: string;
  status: string;
  currency: string;
  bookedTotal: number | null;
  balancedAt: string | null;
  dateCreated: string | null;
}

export type OrderActionKind =
  | 'decision' | 'portal_pull' | 'payment' | 'physical_check'
  | 'vendor_ask' | 'catalog_fix' | 'submit' | 'other';

/**
 * One agent→Matt action: something only Matt can do, put by the orders run
 * instead of narrating "Matt should…" into prose nobody reads. The Actions
 * view lists open ones; Done/Dismiss resolve them; Reply flips one to
 * `answered` (mattNote carries his words) for the next run to consume; the
 * run auto-closes an action when a later pass observes the thing happened.
 */
export interface OrderAction {
  id: string;
  orderId: string | null;
  receiptId: string | null;
  actionKey: string;
  kind: OrderActionKind;
  summary: string;
  nextStep: string | null;
  status: 'open' | 'answered' | 'done' | 'dismissed';
  resolutionNote: string | null;
  /** Matt's own words — set when he answers with "Something else…". */
  mattNote: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  vendor: string | null;
  label: string | null;
  /** When it is needed by (YYYY-MM-DD), and the money at stake — null when the ask names none (or, amount, without orders:read:costs). */
  dueDate: string | null;
  amount: number | null;
  /** The acot receiving the action is about, when receiptId is set. */
  receivingId: number | null;
  /** The errand's upload slot, when the ask expects a document. */
  errand?: { upload?: { doc_hint: OrderDocHint } } | null;
}

/** Done (errands only — an ask with options is answered by choose), Dismiss, or reopen. */
export async function resolveOrderAction(
  actionId: string,
  status: 'done' | 'dismissed' | 'open'
): Promise<{ action: OrderAction }> {
  return apiRequest({
    path: `/orders/actions/${actionId}/resolve`,
    method: 'POST',
    body: { status },
  });
}

export interface OrdersOverview {
  receivings: OverviewReceiving[];
  resolutions: PendingResolution[];
  /** Open nudges across every journey — the Follow-ups view's pending section. */
  tasks: OpenAgentTask[];
  /** Open + answered agent→Matt actions — the Actions view's rows. */
  actions: OrderAction[];
  /** Parcels with no journey — the Follow-ups view's unattributed section. */
  unlinkedPackages: UnlinkedPackage[];
  /** Receipts with no duty verdict, on vendors that do bill duty. */
  dutyUnassessed: DutyUnassessedReceipt[];
  work: WorkCounts;
  counts: {
    receivings: number;
    balanced: number;
    readyToBalance: number;
    withDiscrepancies: number;
    undecidedDiscrepancies: number;
    /** Priced lines whose cost is believable — the pricing list. */
    priceCheckLines: number;
    /** Priced below landed cost: a cost-capture question, not a pricing one. */
    priceCheckSuspect: number;
    /** Of the pricing list, those whose landed cost is settled. */
    priceCheckCostFinal: number;
    pendingResolutions: number;
  };
  /** The same discrepancy rows split by who can act on them. */
  queues: {
    /** Matt: recount, then credit vs let-it-go. The discrepancies table. */
    toSettle: number;
    /** With the warehouse, awaiting a verified count. */
    recount: number;
    /** Sent back to the orders run to re-read the invoice. */
    recapture: number;
    /** Matt: move the lines to the right receiving in the acot backend. */
    toMove: number;
    /** Matt: received, nothing bills it, cause unclear. */
    toReview: number;
    /** Matt: no itemised invoice in email — get it from the portal or the rep. */
    sourceInvoice: number;
    /** The orders run: read/recapture the invoice lines. */
    agentCaptureLines: number;
    /** Nobody: the shipment has not landed. */
    awaitingArrival: number;
    /** Nobody: this vendor never itemises. */
    accepted: number;
  };
}

export async function getOrdersOverview(): Promise<OrdersOverview> {
  return apiRequest({ path: '/orders/overview', method: 'GET' });
}

/** One hidden product named by the live catalog read, for the per-release list. */
export interface HiddenProduct {
  pid: number;
  itemnumber: string | null;
  description: string | null;
  /** Units on the shelf (ACOT store). 0 = nothing to sell yet. */
  available: number;
  catId: number;
}

/** A catalog category a release maps to, as the inventory app resolved it. */
export interface ReleaseCategory {
  cat_id: number;
  name: string | null;
  type: number | null;
  resolved: boolean;
  reason?: string;
  products?: number;
  hidden?: number;
  /** Hidden products that have stock. */
  hidden_in_stock?: number;
  live?: number;
  discontinued?: number;
}

/**
 * Live shop visibility for one release, rolled up across its categories.
 * NULL on the release when it could not be read — never zero. A release whose
 * status is unknown must not render as "nothing hidden".
 */
export interface ReleaseVisibility {
  products: number;
  hidden: number;
  /** Hidden products that are make-live work: all of them until something on
   *  the release is live, then only those with stock. Drives `state`. */
  toMakeLive: number;
  /** Hidden with no stock on an otherwise-live release. Shown, never counted. */
  stragglers: number;
  live: number;
  discontinued: number;
  unresolvedCats: Array<{ catId: number; name: string | null; reason: string }>;
  hiddenProducts: HiddenProduct[];
  hiddenTruncated: boolean;
}

export interface ReleaseRow {
  id: string;
  vendor: string;
  label: string | null;
  status: string;
  embargoDate: string | null;
  expectedAvailability: string | null;
  orderDueDate: string | null;
  placedDate: string | null;
  notes: string | null;
  needsSetup: boolean;
  counts: { newProductLines: number; unresolvedLines: number; releaseNamesMissing: number };
  releaseNames: Array<{
    name: string | null;
    catId: number | null;
    checkedAt: string | null;
    category: ReleaseCategory | null;
  }>;
  setupFolders: Array<{ folder: string; status: string | null; products: number | null; embargo: string | null }>;
  visibility: ReleaseVisibility | null;
  /**
   * unstamped    — no catalog category recorded, so nothing can be checked
   * unknown      — the live catalog read failed
   * unresolvable — the read worked but no stamped category resolves
   * empty        — categories exist but hold no products yet
   * hidden       — products exist and some are still hidden
   * live         — every product in the release is live
   */
  state: 'unstamped' | 'unknown' | 'unresolvable' | 'empty' | 'hidden' | 'live';
}

export interface ReleasesResponse {
  releases: ReleaseRow[];
  /** Non-null when the live catalog read failed; every row's visibility is then null. */
  visibilityError: string | null;
  counts: { total: number; undated: number; hidden: number; live: number; uncheckable: number };
}

/**
 * The Make-live queue: releases by sell embargo, each with how much of it is
 * still hidden on the shop.
 */
export async function getReleases(sinceDays?: number): Promise<ReleasesResponse> {
  return apiRequest({
    path: '/orders/releases',
    method: 'GET',
    query: sinceDays != null ? { since_days: String(sinceDays) } : undefined,
  });
}

/**
 * The price-check rows, split out of the overview (they were 3.7MB of a 4.1MB
 * response refetched on every window focus). Fetched only when the Prices
 * view opens.
 */
export interface PriceChecksResponse {
  priceChecks: PriceCheckRow[];
  work: { prices: number; pricesActionable: number };
  counts: {
    priceCheckLines: number;
    priceCheckSuspect: number;
    priceCheckCostFinal: number;
    priceCheckKept: number;
  };
}

export async function getPriceChecks(): Promise<PriceChecksResponse> {
  return apiRequest({ path: '/orders/price-checks', method: 'GET' });
}

/**
 * CURRENT shelf locations for a batch of pids — read live from the inventory
 * app at request time. The count sheet calls this as it is generated, because
 * the mirrored CountDiscrepancy.location is a snapshot and products get
 * re-shelved. Keys are pids as strings; unshelved pids are absent. Throws when
 * the live read fails — the caller decides whether printing without current
 * locations is acceptable (it is not).
 */
export async function getLiveProductLocations(pids: number[]): Promise<Record<string, string>> {
  const response = await apiRequest<{ locations: Record<string, string> }>({
    path: '/orders/locations',
    method: 'GET',
    query: { pids: pids.join(',') },
  });
  return response.locations ?? {};
}

export interface PriceUpdateResult {
  pid: number;
  price: number;
  ok: boolean;
  error: string | null;
}

export interface PriceUpdateResponse {
  results: PriceUpdateResult[];
  updated: number;
  failed: number;
}

/**
 * Set sell prices on the live acot catalogue — msrp and current price together.
 *
 * One element for a single row, many for the bulk updater; the server takes the
 * same path either way. Results are per-pid and partial success is real: writes
 * that landed stay landed, so read `results` rather than assuming all-or-nothing.
 */
export async function updateProductPrices(
  updates: Array<{ pid: number; price: number }>
): Promise<PriceUpdateResponse> {
  return apiRequest({ path: '/orders/price-updates', method: 'POST', body: { updates } });
}

/**
 * "Keep as is": hide these products from the price check until they are
 * received again. Watermarked server-side at the current max receiving —
 * a new receiving of the same product resurfaces it automatically.
 */
export async function keepPriceChecks(pids: number[]): Promise<{ kept: number }> {
  return apiRequest({ path: '/orders/price-dismissals', method: 'POST', body: { pids } });
}

/** Undo "keep as is" — the products return to the pricing lists. */
export async function restorePriceChecks(pids: number[]): Promise<{ restored: number }> {
  return apiRequest({ path: '/orders/price-dismissals/restore', method: 'POST', body: { pids } });
}

// ============================================================================
// Manual actions — immediate first-class writes and recorded nudges.
// ============================================================================

/**
 * "Mark ordered" on a to-be-ordered journey — fully immediate and real: status
 * and placed_date are first-class columns, the optional order number becomes a
 * ref (which is what lets the incoming confirmation auto-attach instead of
 * fuzzy-matching), and a placed event records that the advance was manual.
 * The ORDERED document stays honestly empty; hunting it remains the run's job.
 */
export async function markOrdered(
  id: string,
  opts: { placedDate: string; orderNumber?: string | null }
): Promise<void> {
  await apiRequest({
    path: `/orders/${encodeURIComponent(id)}`,
    method: 'PATCH',
    query: { compact: 'true' },
    body: {
      status: 'placed',
      placed_date: opts.placedDate,
      ...(opts.orderNumber ? { refs: [{ ref_type: 'order_no', value: opts.orderNumber }] } : {}),
      event: {
        event_type: 'placed',
        event_date: opts.placedDate,
        summary: opts.orderNumber
          ? `Marked ordered from the Orders page (order #${opts.orderNumber})`
          : 'Marked ordered from the Orders page',
        data: { manual: true },
      },
    },
  });
}

/**
 * Attach a parcel to a journey by writing its tracking number as a ref.
 *
 * The ref IS the link: every order write runs linkPackagesToOrder, which matches
 * the journey's tracking_no refs against the packages table and stamps
 * vendor_order_id. So this one PATCH both records the attribution and lights up
 * the live-tracking chip on the journey's row — no separate package write.
 */
export async function attachPackageToOrder(
  orderId: string,
  pkg: { trackingCode: string; carrier: string | null; vendor: string | null }
): Promise<void> {
  await apiRequest({
    path: `/orders/${encodeURIComponent(orderId)}`,
    method: 'PATCH',
    query: { compact: 'true' },
    body: {
      refs: [{ ref_type: 'tracking_no', value: pkg.trackingCode }],
      event: {
        event_type: 'shipment',
        summary: `Parcel attributed from the Orders page: ${[pkg.carrier, pkg.trackingCode]
          .filter(Boolean).join(' ')}${pkg.vendor ? ` (labelled ${pkg.vendor})` : ''}`,
        data: { manual: true, tracking_code: pkg.trackingCode },
      },
    },
  });
}

/** "I cancelled this order" — status is first-class, so this is immediate. */
export async function cancelOrder(id: string): Promise<void> {
  await apiRequest({
    path: `/orders/${encodeURIComponent(id)}`,
    method: 'PATCH',
    query: { compact: 'true' },
    body: {
      status: 'cancelled',
      event: { event_type: 'note', summary: 'Cancelled — marked manually from the Orders page', data: { manual: true } },
    },
  });
}

/**
 * Reopen an archived or won't-order journey. A won't-order one goes back to
 * the to-order list with its draft active again; the server turns an ended
 * sale's date into a plain (late) order-by so the sale rule leaves it open.
 */
export async function reopenOrder(id: string): Promise<void> {
  await apiRequest({
    path: `/orders/${encodeURIComponent(id)}/reopen`,
    method: 'POST',
  });
}

/** Inline pencil edits for the deadline ladder — PATCH-able today, immediate. */
export async function patchOrderDates(
  id: string,
  dates: { order_due_date?: string; expected_availability?: string }
): Promise<void> {
  await apiRequest({
    path: `/orders/${encodeURIComponent(id)}`,
    method: 'PATCH',
    query: { compact: 'true' },
    body: dates,
  });
}

/** Record a nudge for the next orders run. Duplicate open nudges collapse server-side. */
export async function createOrderTask(
  orderId: string,
  task: { kind: AgentTaskKind; payload?: Record<string, unknown>; note?: string | null }
): Promise<AgentTask> {
  const response = await apiRequest<{ task: AgentTask }>({
    path: `/orders/${encodeURIComponent(orderId)}/tasks`,
    method: 'POST',
    body: { kind: task.kind, payload: task.payload ?? {}, note: task.note ?? null },
  });
  return response.task;
}

/**
 * "Ask vendor": an unsent reply draft in Mail on the journey's vendor contact
 * message (the server picks it). `document` asks for a missing document,
 * `claim` is a credit claim. 409 no_vendor_message when the journey has none.
 */
export async function createVendorDraft(
  orderId: string,
  draft: { purpose: 'document' | 'claim'; text: string }
): Promise<{ draft_id: string; to: string; open_url: string }> {
  return apiRequest({
    path: `/orders/${encodeURIComponent(orderId)}/vendor-drafts`,
    method: 'POST',
    body: draft,
  });
}

/** Withdraw a nudge the run has not consumed yet. */
export async function deleteOrderTask(taskId: string): Promise<void> {
  await apiRequest({
    path: `/orders/tasks/${encodeURIComponent(taskId)}`,
    method: 'DELETE',
  });
}

/**
 * Upload a file straight onto a journey. The file lands immediately (chip on
 * the journey, "awaiting processing"); parsing into the proper document is the
 * next run's job via the process_document task this returns.
 */
export async function uploadOrderDocument(
  orderId: string,
  file: File,
  opts: { docHint?: OrderDocHint; note?: string | null } = {}
): Promise<{ document: OrderDocument; task: AgentTask }> {
  const formData = new FormData();
  formData.append('file', file);
  if (opts.docHint) formData.append('doc_hint', opts.docHint);
  if (opts.note) formData.append('note', opts.note);
  return apiRequest({
    path: `/orders/${encodeURIComponent(orderId)}/documents`,
    method: 'POST',
    formData,
  });
}
